import { useMemo, useState, useEffect } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CaretDown, CalendarBlank, Users, ListBullets, SquaresFour } from '@phosphor-icons/react';

/**
 * IntakeAccordion — groups students by their `intake_label` (falling back to
 * "No intake assigned") and renders one collapsible per intake with a rich
 * header showing count + pipeline-stage breakdown chips.
 *
 * Props
 * ─────────────────────────────────────────────────────────────────────────
 *  students       Array of student objects. Each is expected to have
 *                 `intake_label` (e.g. "Jul 2026") and optionally
 *                 `intake_date` (YYYY-MM-DD) which is used to sort groups
 *                 chronologically. If `intake_label` is missing the student
 *                 falls into the "No intake assigned" bucket which is
 *                 always shown last.
 *  stages         Ordered list of pipeline stage keys — used to control
 *                 the order of the breakdown chips.
 *  stageMeta(key) Function returning `{ label, className }` for a stage.
 *                 The `className` is applied to a small pill chip.
 *  renderGroup    fn(groupStudents, groupMeta) → JSX for the group content.
 *                 Caller controls rendering (table / cards / etc.).
 *  storageKey     Optional key to persist which panels are open across
 *                 remounts (per user session — uses localStorage).
 *  emptyMessage   String shown when no students exist.
 *  tone           Visual tone of the accordion header. One of:
 *                   `default` (indigo/white) — for light backgrounds
 *                   `light`   (subtle white glass) — for dark backgrounds
 */
export default function IntakeAccordion({
  students,
  stages = [],
  stageMeta = () => ({ label: '', className: 'bg-gray-100 text-gray-600 border-gray-200' }),
  renderGroup,
  renderGroupExtra,   // optional: fn(groupStudents) → JSX shown in the header (opposite the title)
  storageKey,
  emptyMessage = 'No students to show.',
  tone = 'default',
  variant = 'default', // 'default' | 'editorial'
}) {
  const groups = useMemo(() => groupByIntake(students), [students]);

  // Persist open state per storageKey. Default = all collapsed.
  const [openValues, setOpenValues] = useState(() => {
    if (!storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  });
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(openValues)); } catch { /* ignore */ }
  }, [openValues, storageKey]);

  if (!students || students.length === 0) {
    return (
      <div className={variant === 'editorial'
        ? 'border border-dashed border-gray-200 bg-white px-6 py-16 text-center text-[13px] text-gray-500'
        : 'rounded-2xl border border-dashed border-gray-200 bg-white/60 px-6 py-12 text-center text-sm text-gray-500'
      } data-testid="intake-accordion-empty">
        {emptyMessage}
      </div>
    );
  }

  const isLight = tone === 'light';
  const isEditorial = variant === 'editorial';

  // Editorial variant — monochrome, hairline, serif label, no colored accent cycle
  if (isEditorial) {
    return (
      <AccordionPrimitive.Root
        type="multiple"
        value={openValues}
        onValueChange={setOpenValues}
        className="border border-gray-200 bg-white divide-y divide-gray-200"
        data-testid="intake-accordion"
      >
        {groups.map((g) => {
          const breakdown = summariseStages(g.students, stages);
          return (
            <AccordionPrimitive.Item
              key={g.key}
              value={g.key}
              data-testid={`intake-group-${g.key}`}
            >
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger
                  className="group flex w-full items-center gap-5 px-6 py-5 text-left transition-colors hover:bg-gray-50/70"
                  data-testid={`intake-trigger-${g.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 min-w-0">
                      <span
                        className="truncate text-[22px] leading-none font-semibold tracking-tight text-gray-900"
                        style={{ fontFamily: "'Libre Baskerville', serif" }}
                      >
                        {g.label}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">
                        {g.students.length} student{g.students.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {/* Stage breakdown — dot + label + count */}
                    <div className="mt-3 flex items-center gap-x-5 gap-y-1.5 flex-wrap">
                      {breakdown.length === 0 ? (
                        <span className="text-[12px] text-gray-400">No pipeline data</span>
                      ) : breakdown.map(({ stage, count }) => {
                        const meta = stageMeta(stage);
                        return (
                          <span
                            key={stage}
                            className="inline-flex items-center gap-2 text-[12px] text-gray-600"
                            title={meta.label}
                          >
                            <span aria-hidden className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot || 'bg-gray-400'}`} />
                            <span className="truncate max-w-[180px]">{meta.label}</span>
                            <span className="tabular-nums font-semibold text-gray-900">{count}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {renderGroupExtra ? (
                    <div
                      className="shrink-0 hidden md:flex items-center"
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      data-testid={`intake-extra-${g.key}`}
                    >
                      {renderGroupExtra(g.students, g)}
                    </div>
                  ) : null}

                  <span
                    aria-hidden
                    className="shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 transition-transform duration-300 ease-out group-data-[state=open]:rotate-180"
                  >
                    <CaretDown size={13} weight="bold" />
                  </span>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>

              <AccordionPrimitive.Content
                className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
                data-testid={`intake-content-${g.key}`}
              >
                <div className="border-t border-gray-200 bg-gray-50/40">
                  {renderGroup(g.students, g)}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          );
        })}
      </AccordionPrimitive.Root>
    );
  }

  return (
    <AccordionPrimitive.Root
      type="multiple"
      value={openValues}
      onValueChange={setOpenValues}
      className="space-y-3"
      data-testid="intake-accordion"
    >
      {groups.map((g, idx) => {
        const breakdown = summariseStages(g.students, stages);
        const accent = ACCENT_CYCLE[idx % ACCENT_CYCLE.length];
        return (
          <AccordionPrimitive.Item
            key={g.key}
            value={g.key}
            className={`group/intake overflow-hidden rounded-2xl border shadow-sm transition-all data-[state=open]:shadow-md ${isLight ? 'border-white/15 bg-white/[0.04] backdrop-blur data-[state=open]:bg-white/[0.06]' : `bg-white ${accent.border} data-[state=open]:${accent.borderOpen}`}`}
            data-testid={`intake-group-${g.key}`}
          >
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger
                className={`group flex w-full items-center gap-3 px-4 sm:px-5 py-4 text-left transition-colors ${isLight ? 'text-white hover:bg-white/[0.05]' : `${accent.headerBg} hover:${accent.headerHover}`}`}
                data-testid={`intake-trigger-${g.key}`}
              >
                {/* Accent gradient bar */}
                <span aria-hidden className={`shrink-0 w-1 h-11 rounded-full ${isLight ? 'bg-gradient-to-b from-white/70 to-white/20' : accent.bar}`} />

                {/* Icon */}
                <span className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${isLight ? 'bg-white/10 text-white border border-white/15' : `${accent.iconBg} ${accent.iconText} border ${accent.iconBorder}`}`}>
                  <CalendarBlank size={18} weight="fill" />
                </span>

                {/* Title */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2.5 min-w-0">
                    <span className={`truncate font-semibold text-[18px] tracking-tight ${isLight ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: "Calibri, 'Segoe UI', system-ui, -apple-system, sans-serif" }}>
                      {g.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-md ${isLight ? 'text-white/80 bg-white/10 border border-white/10' : `${accent.countBg} ${accent.countText}`}`} style={{ fontFamily: "Calibri, 'Segoe UI', system-ui, -apple-system, sans-serif" }}>
                      <Users size={11} weight="fill" /> {g.students.length}
                    </span>
                  </div>

                  {/* Stage breakdown chips */}
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {breakdown.length === 0 ? (
                      <span className={`text-[13px] ${isLight ? 'text-white/60' : 'text-gray-400'}`} style={{ fontFamily: "Calibri, 'Segoe UI', system-ui, -apple-system, sans-serif" }}>No pipeline data</span>
                    ) : breakdown.map(({ stage, count }) => {
                      const meta = stageMeta(stage);
                      return (
                        <span
                          key={stage}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium border transition-transform group-hover:-translate-y-0.5 ${isLight ? 'bg-white/10 text-white border-white/10' : meta.className}`}
                          style={{ fontFamily: "Calibri, 'Segoe UI', system-ui, -apple-system, sans-serif" }}
                          title={meta.label}
                        >
                          <span className="truncate max-w-[160px]">{meta.label}</span>
                          <span className="tabular-nums opacity-70">·</span>
                          <span className="tabular-nums font-semibold">{count}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Optional consumer-supplied action (e.g. "Select all in intake").
                    Visible on ALL breakpoints — mobile registrars need this
                    chip to bulk-select without scrolling through 250+ cards. */}
                {renderGroupExtra ? (
                  <div
                    className="shrink-0 flex items-center"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid={`intake-extra-${g.key}`}
                  >
                    {renderGroupExtra(g.students, g)}
                  </div>
                ) : null}

                {/* Chevron */}
                <span
                  aria-hidden
                  className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 ease-out group-data-[state=open]:rotate-180 ${isLight ? 'text-white/70 group-hover:text-white bg-white/5' : `${accent.iconText} ${accent.iconBg}`}`}
                >
                  <CaretDown size={14} weight="bold" />
                </span>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionPrimitive.Content
              className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
              data-testid={`intake-content-${g.key}`}
            >
              <div className={`border-t ${isLight ? 'border-white/10' : accent.border}`}>
                {/* Mobile-only "Select all" bar at the top of the expanded
                    content — desktop already has it in the header row. On
                    small screens we duplicate it here so the registrar can
                    tap it without scrolling back to the header. */}
                {renderGroupExtra ? (
                  <div
                    className="md:hidden flex items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-200 bg-gray-50/60"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid={`intake-extra-mobile-${g.key}`}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                      Bulk actions
                    </span>
                    {renderGroupExtra(g.students, g)}
                  </div>
                ) : null}
                {renderGroup(g.students, g)}
              </div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        );
      })}
    </AccordionPrimitive.Root>
  );
}

/* ───────────────────────── Helpers ───────────────────────── */

// Order accents cycle through the visible groups so consecutive intakes look
// distinct without being overwhelming.
const ACCENT_CYCLE = [
  {
    border: 'border-indigo-200',
    borderOpen: 'border-indigo-300',
    headerBg: 'bg-gradient-to-r from-indigo-50/70 via-white to-indigo-50/30',
    headerHover: 'bg-indigo-50',
    bar: 'bg-gradient-to-b from-indigo-500 to-indigo-300',
    iconBg: 'bg-indigo-50',
    iconText: 'text-indigo-700',
    iconBorder: 'border-indigo-100',
    countBg: 'bg-indigo-100/70',
    countText: 'text-indigo-800',
  },
  {
    border: 'border-emerald-200',
    borderOpen: 'border-emerald-300',
    headerBg: 'bg-gradient-to-r from-emerald-50/70 via-white to-emerald-50/30',
    headerHover: 'bg-emerald-50',
    bar: 'bg-gradient-to-b from-emerald-500 to-emerald-300',
    iconBg: 'bg-emerald-50',
    iconText: 'text-emerald-700',
    iconBorder: 'border-emerald-100',
    countBg: 'bg-emerald-100/70',
    countText: 'text-emerald-800',
  },
  {
    border: 'border-amber-200',
    borderOpen: 'border-amber-300',
    headerBg: 'bg-gradient-to-r from-amber-50/70 via-white to-amber-50/30',
    headerHover: 'bg-amber-50',
    bar: 'bg-gradient-to-b from-amber-500 to-amber-300',
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-700',
    iconBorder: 'border-amber-100',
    countBg: 'bg-amber-100/70',
    countText: 'text-amber-800',
  },
  {
    border: 'border-sky-200',
    borderOpen: 'border-sky-300',
    headerBg: 'bg-gradient-to-r from-sky-50/70 via-white to-sky-50/30',
    headerHover: 'bg-sky-50',
    bar: 'bg-gradient-to-b from-sky-500 to-sky-300',
    iconBg: 'bg-sky-50',
    iconText: 'text-sky-700',
    iconBorder: 'border-sky-100',
    countBg: 'bg-sky-100/70',
    countText: 'text-sky-800',
  },
  {
    border: 'border-violet-200',
    borderOpen: 'border-violet-300',
    headerBg: 'bg-gradient-to-r from-violet-50/70 via-white to-violet-50/30',
    headerHover: 'bg-violet-50',
    bar: 'bg-gradient-to-b from-violet-500 to-violet-300',
    iconBg: 'bg-violet-50',
    iconText: 'text-violet-700',
    iconBorder: 'border-violet-100',
    countBg: 'bg-violet-100/70',
    countText: 'text-violet-800',
  },
  {
    border: 'border-rose-200',
    borderOpen: 'border-rose-300',
    headerBg: 'bg-gradient-to-r from-rose-50/70 via-white to-rose-50/30',
    headerHover: 'bg-rose-50',
    bar: 'bg-gradient-to-b from-rose-500 to-rose-300',
    iconBg: 'bg-rose-50',
    iconText: 'text-rose-700',
    iconBorder: 'border-rose-100',
    countBg: 'bg-rose-100/70',
    countText: 'text-rose-800',
  },
];

function groupByIntake(students) {
  const map = new Map();
  for (const s of students || []) {
    const rawLabel = (s.intake_label || '').trim();
    const key = rawLabel ? `intake:${rawLabel}` : 'intake:__unassigned__';
    if (!map.has(key)) {
      // Derive sortKey from the LABEL first (which is guaranteed identical
      // for every student in the group). Only fall back to `intake_date`
      // when the label can't be parsed — this prevents one student with a
      // typo'd intake_date (e.g. wrong year) from re-ordering the whole
      // intake ahead of newer ones.
      const labelKey = rawLabel ? deriveSortKeyFromLabel(rawLabel) : '';
      map.set(key, {
        key,
        label: rawLabel || 'No intake assigned',
        sortKey: labelKey || s.intake_date || '',
        students: [],
      });
    }
    map.get(key).students.push(s);
  }
  // Sort: unassigned last, then most-recent/future intakes first.
  return Array.from(map.values()).sort((a, b) => {
    if (a.key === 'intake:__unassigned__') return 1;
    if (b.key === 'intake:__unassigned__') return -1;
    // Larger sortKey (e.g. 2026-10-01 > 2026-07-01) comes first.
    return (b.sortKey || '').localeCompare(a.sortKey || '');
  });
}

// If the API only ships `intake_label` like "Jul 2026" (no date), derive a
// sortable YYYY-MM string so July < October ordering still works.
const MONTHS = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
function deriveSortKeyFromLabel(label) {
  const m = /^([A-Za-z]{3})[a-z]*\s+(\d{4})$/.exec(label.trim());
  if (!m) return '';
  const mm = MONTHS[m[1].toLowerCase()];
  return mm ? `${m[2]}-${mm}-01` : '';
}

function summariseStages(students, stages) {
  const counts = new Map();
  for (const s of students) {
    const key = s.application_stage || '__unknown__';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  // Preserve caller's stage ordering; unknown/other stages come after.
  const ordered = [];
  for (const stg of stages) {
    if (counts.has(stg)) { ordered.push({ stage: stg, count: counts.get(stg) }); counts.delete(stg); }
  }
  for (const [stg, count] of counts) ordered.push({ stage: stg, count });
  return ordered;
}

/* ───────────────────────── Toggle chip ───────────────────────── */

export function GroupByIntakeToggle({ value, onChange, storageKey, tone = 'default', testId = 'group-by-intake-toggle' }) {
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* ignore */ }
  }, [value, storageKey]);

  const isLight = tone === 'light';
  return (
    <div
      className={`inline-flex items-center rounded-md p-0.5 border ${isLight ? 'bg-white/10 border-white/15' : 'bg-white border-gray-200'}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded ${value ? (isLight ? 'bg-white text-indigo-700 shadow-sm' : 'bg-gray-900 text-white') : (isLight ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900')}`}
        data-testid={`${testId}-on`}
        title="Group students by intake"
      >
        <SquaresFour size={11} weight="fill" /> By intake
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded ${!value ? (isLight ? 'bg-white text-indigo-700 shadow-sm' : 'bg-gray-900 text-white') : (isLight ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900')}`}
        data-testid={`${testId}-off`}
        title="Show all students as a flat list"
      >
        <ListBullets size={11} weight="fill" /> Flat list
      </button>
    </div>
  );
}

export function useGroupByIntake(storageKey, defaultValue = true) {
  const [value, setValue] = useState(() => {
    if (!storageKey) return defaultValue;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw !== null && raw !== undefined) return JSON.parse(raw);
    } catch { /* ignore */ }
    return defaultValue;
  });
  return [value, setValue];
}
